/**
 * Pure helpers for the placement combination workspace (docs/specs/placement-combination-workspace.md).
 *
 * Deliberately free of ApiClient/axios imports: those are ESM and break Jest's parser, which is why
 * App.test.js and TrackingInput.test.js cannot run. Keeping the basket arithmetic here means the
 * numbers in the header -- the ones the operator trusts -- are actually covered by tests.
 */

/** Percentage-point delta, always signed so a zero-gain draw reads as deliberate, not missing. */
export function formatGain(pp) {
    if (pp === null || pp === undefined) return '—';
    const n = Number(pp);
    if (!Number.isFinite(n)) return '—';
    return `${n > 0 ? '+' : ''}${n.toFixed(1)}pp`;
}

/** Freshness of a draw, in the operator's terms rather than raw hours. */
export function describeAge(ageHours, expired, confirmableUntil) {
    if (ageHours === null || ageHours === undefined) return 'age unknown';
    const h = Number(ageHours);
    if (expired) return `expired · ${h < 48 ? `${h}h old` : `${Math.floor(h / 24)}d old`}`;
    // Owner, 2026-09-06: when Amazon's own deadline is stored, "left" is REAL remaining time —
    // the 36-minus-age arithmetic survives only for legacy draws without the field.
    const until = Date.parse(String(confirmableUntil || ''));
    if (Number.isFinite(until)) {
        const leftH = Math.max(0, Math.round((until - Date.now()) / 3_600_000));
        return h < 1 ? 'minutes old' : `${h}h old · ${leftH}h left`;
    }
    if (h < 1) return 'minutes old';
    if (h < 24) return `${h}h old · ${36 - h}h left`;
    return `${h}h old · ${Math.max(0, 36 - h)}h left`;
}

/**
 * The union of FCs a basket would place into, given today's stocked FCs as the base.
 * Exported for tests: the basket header is only trustworthy if this matches the rows.
 */
export function basketUnion(currentFcs, groups, selectedRunIds) {
    const union = new Set(currentFcs || []);
    const picked = new Set(selectedRunIds || []);
    (groups || []).forEach(g => {
        const inBasket = (g.variants || []).some(v => picked.has(v.runId));
        if (inBasket) (g.fcs || []).forEach(fc => union.add(fc));
    });
    return Array.from(union).sort();
}

/** Units committed by the chosen variants. */
export function basketUnits(groups, selectedRunIds) {
    const picked = new Set(selectedRunIds || []);
    let units = 0;
    (groups || []).forEach(g => (g.variants || []).forEach(v => {
        if (picked.has(v.runId)) units += (v.units || 0);
    }));
    return units;
}

/**
 * The FC x plan matrix — owner, 2026-08-25: "there you should clearly see the shipping plan FC
 * combinations".
 *
 * Columns are ordered by how many plans cover each FC, most-covered first. That ordering is the
 * point of the view: the FCs every plan contains form a solid block on the left (GEG2 is in 20 of
 * one ASIN's 68 draws — buying it three times is the mistake this page exists to prevent), and the
 * sparse right-hand tail is where the plans actually differ from each other. `alreadyStocked`
 * marks the columns that are worth nothing new whoever picks them.
 */
export function buildFcMatrix(currentFcs, groups, currentFcUnits, inboundFcUnits) {
    const stocked = new Set(currentFcs || []);
    const stockUnits = currentFcUnits || {};
    const inbound = inboundFcUnits || {};
    const rows = (groups || []).map(g => ({
        key: (g.fcs || []).join('-'),
        fcs: new Set(g.fcs || []),
        // The cheapest live variant is what a reach-first basket would actually spend on this set.
        variants: (g.variants || []).filter(v => !v.expired),
        marginalGain1Day: g.marginalGain1Day,
        newCount: (g.newFcs || []).length,
        // Owner, 2026-08-26: "the FC matrix should show a badge for the recommended shipments to
        // take" -- the backend sets this when any live variant was scored TAKE.
        recommended: !!g.recommended,
    }));

    const coverCount = new Map();
    rows.forEach(r => r.fcs.forEach(fc => coverCount.set(fc, (coverCount.get(fc) || 0) + 1)));
    // Owner, 2026-08-26: "so we understand where other shipments are happening" -- an FC that a
    // confirmed shipment is heading to gets a column even when no plan here touches it. Zero
    // plan-coverage sorts these after every plan column: they are context, not choices.
    Object.keys(inbound).forEach(fc => { if (!coverCount.has(fc)) coverCount.set(fc, 0); });

    const columns = Array.from(coverCount.keys())
        .sort((a, b) => (coverCount.get(b) - coverCount.get(a)) || a.localeCompare(b))
        .map(fc => ({
            fc,
            plans: coverCount.get(fc),
            alreadyStocked: stocked.has(fc),
            // Units currently at the FC -- shown in the stocked row instead of a mark. Null when
            // the ledger gave no figure, so the cell can fall back honestly.
            stockUnits: typeof stockUnits[fc] === 'number' ? stockUnits[fc] : null,
            // Units on confirmed shipments still in transit to the FC. Null (not 0) when nothing
            // is inbound, so the row can leave the cell blank.
            inboundUnits: typeof inbound[fc] === 'number' ? inbound[fc] : null,
        }));

    return { columns, rows };
}

/**
 * The variant a matrix row STANDS FOR right now: the one already picked into the basket, else the
 * smallest live one (what a reach-first basket would spend). Both the row's select control and its
 * per-FC unit cells read this, so what you toggle is always the same thing the cells describe.
 */
export function displayVariant(variants, selectedRunIds) {
    const picked = new Set(selectedRunIds || []);
    const live = (variants || []).filter(v => !v.expired);
    return live.find(v => picked.has(v.runId))
        || live.reduce((a, b) => (a === null || b.units < a.units ? b : a), null);
}

/**
 * One selected plan's snapshot for the "selected plans" section — captured at pick time so the
 * row survives the reload that follows a confirm (a confirmed run leaves the open-draw pool, but
 * its Confirm/print row must not vanish mid-action).
 */
export function planSnapshot(groups, runId) {
    for (const g of groups || []) {
        const v = (g.variants || []).find(x => x.runId === runId);
        if (v) {
            return {
                runId: v.runId,
                inboundPlanId: v.inboundPlanId || null,
                placementOptionId: v.placementOptionId || null,
                units: v.units || 0,
                fcs: [...(g.fcs || [])],
                recommended: !!g.recommended,
                expired: !!v.expired,
            };
        }
    }
    return null;
}


/**
 * Pins for the FC map that appears above the selected-plans section (owner, 2026-08-26: "the FC
 * map should appear above the selected plans section showing the current FCs, inbound FCs, and
 * selected plan FCs"). One entry per FC named by ANY of the three sources, tagged with all of
 * what it is — an FC can hold stock, have a shipment inbound, and sit in a selected plan at once.
 *
 * An FC the location dictionary cannot place goes to `unknown` instead of `pins` — listed under
 * the map, never silently dropped (the repaired FC dictionary still has coordinate gaps).
 */
export function buildFcMapPins(locations, currentFcUnits, inboundFcUnits, selectedPlanFcs) {
    const loc = locations || {};
    const cur = currentFcUnits || {};
    const inb = inboundFcUnits || {};
    const sel = new Set(selectedPlanFcs || []);
    const codes = new Set([...Object.keys(cur), ...Object.keys(inb), ...sel]);

    const pins = [];
    const unknown = [];
    for (const fc of [...codes].sort()) {
        const l = loc[fc];
        const entry = {
            fc,
            stockUnits: typeof cur[fc] === 'number' ? cur[fc] : null,
            inboundUnits: typeof inb[fc] === 'number' ? inb[fc] : null,
            selected: sel.has(fc),
            city: l?.city ?? null,
            state: l?.state ?? null,
        };
        if (typeof l?.lat === 'number' && typeof l?.lng === 'number') {
            pins.push({ ...entry, lat: l.lat, lng: l.lng });
        } else {
            unknown.push(entry);
        }
    }
    return { pins, unknown };
}
